import { types, flow } from 'mobx-state-tree';
import Axios from 'axios';

// Use the Murphy axios instance for local backend
const murphyAxios = Axios.create({
  baseURL: process.env.REACT_APP_API_BASE_URL || 'http://localhost:3001/dev'
});

// Will be set by UserContextProvider
let getAccessTokenFunction = null;

export const configureMeetingsAuth = (getAccessTokenSilently) => {
  getAccessTokenFunction = getAccessTokenSilently;
};

// Authorization interceptor
murphyAxios.interceptors.request.use(async (config) => {
  if (getAccessTokenFunction) {
    try {
      const accessToken = await getAccessTokenFunction();
      config.headers.Authorization = `Bearer ${accessToken}`;
    } catch (error) {
      console.error('Failed to get access token:', error);
    }
  }
  return config;
});

const Meeting = types.model('Meeting', {
  id: types.maybeNull(types.string),
  botId: types.maybeNull(types.string), // Some records might use botId instead of id
  recallBotId: types.maybeNull(types.string),
  calendarEventId: types.maybeNull(types.string),
  icalUid: types.maybeNull(types.string),
  meetingUrl: types.maybeNull(types.string),
  meetingTitle: types.maybeNull(types.string),
  scheduledStartTime: types.maybeNull(types.string),
  status: types.string,
  validatedParticipants: types.optional(types.array(types.string), []),
  approvalRequestId: types.maybeNull(types.string),
  createdAt: types.maybeNull(types.string),
  updatedAt: types.maybeNull(types.string),
  metadata: types.optional(types.frozen(), {})
});

export const MeetingsStore = types
  .model('MeetingsStore', {
    meetings: types.array(Meeting),
    statistics: types.optional(types.frozen(), {}),
    loading: types.optional(types.boolean, false),
    error: types.maybeNull(types.string)
  })
  .actions(self => ({
    loadMeetings: flow(function* (filters = {}) {
      self.loading = true;
      self.error = null;

      try {
        const params = new URLSearchParams();
        
        if (filters.status) params.append('status', filters.status);
        if (filters.limit) params.append('limit', filters.limit);

        const response = yield murphyAxios.get(`/murphy/meetings?${params.toString()}`);

        self.meetings = response.data.meetings || [];
        self.statistics = {
          ...response.data.statistics,
          total: response.data.total
        };

        console.log('Loaded meetings:', {
          count: self.meetings.length,
          statistics: self.statistics
        });
      } catch (error) {
        console.error('Failed to load meetings:', error);
        self.error = error.message;
        self.meetings = [];
        self.statistics = {};
      } finally {
        self.loading = false;
      }
    })
  }))
  .views(self => ({
    get scheduledMeetings() {
      return self.meetings.filter(m => m.status === 'scheduled');
    },
    get inProgressMeetings() {
      return self.meetings.filter(m => m.status === 'in_progress');
    },
    get completedMeetings() {
      return self.meetings.filter(m => m.status === 'completed');
    },
    get failedMeetings() {
      return self.meetings.filter(m => m.status === 'failed');
    }
  }));
